import React, { useCallback, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router'
import {
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ENTITY_CUSTOMER,
  ROUTE_CUSTOMERS
} from '@klear/constants'
import { CustomerWizard } from '@klear/customer-ui/customer-wizard'
import type { CustomerStepGroupResult } from '@klear/customer-ui/customer-step-group'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { returnUrlFromSearchParams } from '@klear/ui/wizard/return-url'
import { useCustomer } from '@klear/customer-ui/use-customers'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import toInteger from 'lodash/toInteger'

export type CustomerWizardPageMode = 'create' | 'view' | 'edit'

export interface CustomerWizardPageProps {
  mode: CustomerWizardPageMode
}

export default function CustomerWizardPage({ mode }: CustomerWizardPageProps) {
  const { customerId, traderId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  const id = customerId ? toInteger(customerId) : 0
  const tid = traderId ? toInteger(traderId) : 0
  const wizardTraderId = mode !== 'create' && tid > 0 ? tid : undefined
  const { customer } = useCustomer(id, { enabled: mode !== 'create', traderId: wizardTraderId })
  useDocumentTitle(customer?.name, { create: 'New Customer', edit: 'Edit Customer', view: 'Customer' }[mode])

  const returnTo = useMemo(() => returnUrlFromSearchParams(new URLSearchParams(location.search)), [location.search])

  const canEditCustomer = !!user?.hasPermission(PERMISSION_ENTITY_CUSTOMER, PERMISSION_ACTION_EDIT)
  const canViewCustomerEntity = !!user?.hasPermission(PERMISSION_ENTITY_CUSTOMER, PERMISSION_ACTION_VIEW)

  const canViewCustomerCreditRatings = mode === 'view' ? true : canViewCustomerEntity
  const canEditCustomerCreditRatings = mode === 'edit' ? true : canEditCustomer

  const listPath = `/${ROUTE_CUSTOMERS}`
  const exitToList = useCallback(() => navigate(listPath), [navigate, listPath])
  const exitAfterEdit = useCallback(() => navigate(returnTo ?? listPath), [navigate, returnTo, listPath])
  const handleBackDone = mode === 'edit' ? exitAfterEdit : exitToList

  const goToEdit = useCallback(() => navigate(`/${ROUTE_CUSTOMERS}/${id}/${PERMISSION_ACTION_EDIT}`), [navigate, id])
  const handleDone = useCallback(
    (result: CustomerStepGroupResult) => {
      if (mode === 'edit' && result.customerId !== id) {
        navigate(`/${ROUTE_CUSTOMERS}/${result.customerId}`)
        return
      }
      handleBackDone()
    },
    [mode, id, navigate, handleBackDone]
  )

  return (
    <CustomerWizard
      mode={mode}
      customerId={mode !== 'create' ? id : undefined}
      traderId={wizardTraderId}
      showTraderSelect={mode === 'create'}
      showIdentifiers
      showParentOrganization
      showCompanyLookup={mode === 'create' || mode === 'edit'}
      canEdit={mode === 'view' ? canEditCustomer : true}
      canViewCustomerCreditRatings={canViewCustomerCreditRatings}
      canEditCustomerCreditRatings={canEditCustomerCreditRatings}
      onEdit={mode === 'view' ? goToEdit : undefined}
      onBack={handleBackDone}
      onDone={handleDone}
    />
  )
}
